import React from 'react';
import { Link } from 'react-router-dom';
import { FaWhatsapp, FaApple, FaGooglePlay, FaArrowLeft } from 'react-icons/fa';
import { MessageCircle } from 'lucide-react';
import rxLogo from '../Images/home/RxOne_Color.png';
import appScreenshot from '../Images/home/treatment_app_screen.png';
import fortisLogo from '../Images/home/logo.png';
import { Helmet } from 'react-helmet';

const ThankYouPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">

<Helmet>
        <title>Thank You for Contacting Us - Fortis Hospital | RxOne</title>
        <meta
          name="description"
          content="Thank you for reaching out to Fortis Hospital. Our team will contact you shortly to discuss your healthcare needs."
        />
        <meta property="og:title" content="Thank You for Contacting Us - Fortis Hospital | RxOne" />
        <meta property="og:description" content="Thank you for reaching out to Fortis Hospital. Our team will contact you shortly to discuss your healthcare needs." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rxone.app/hospitals/fortis/thank-you" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Thank You for Contacting Us - Fortis Hospital | RxOne" />
        <meta name="twitter:description" content="Thank you for reaching out to Fortis Hospital. Our team will contact you shortly to discuss your healthcare needs." />
        <meta name="keywords" content="Fortis Hospital, thank you page, healthcare inquiry, medical consultation, RxOne, patient support" />
        <link rel="canonical" href="https://rxone.app/hospitals/fortis/thank-you" />
      </Helmet>
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Link to="/hospitals/fortis">
            <img src={fortisLogo} alt="Fortis Logo" className="h-10 md:h-12" />
          </Link>
          <img src={rxLogo} alt="RxOne Logo" className="h-8 md:h-10" />
        </div>
      </header>

      <main className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Thank You for Contacting Us!</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We've received your request and our team will get back to you shortly.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md overflow-hidden mb-12">
            <div className="md:flex">
              <div className="md:flex-1 p-8 md:p-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">What Happens Next?</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 bg-green-100 rounded-full size-10 flex items-center justify-center">
                      <span className="text-green-600 font-bold">1</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Our Team Reviews Your Request</h3>
                      <p className="text-gray-600">Our medical coordination team will review your information and prepare personalized options.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 bg-green-100 rounded-full size-10 flex items-center justify-center">
                      <span className="text-green-600 font-bold">2</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">We'll Contact You</h3>
                      <p className="text-gray-600">A medical coordinator will reach out to you within 24 hours to discuss next steps.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-star gap-2">
                    <div className="flex-shrink-0 bg-green-100 rounded-full size-10 flex items-center justify-center">
                      <span className="text-green-600 font-bold">3</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Personalized Treatment Plan</h3>
                      <p className="text-gray-600">We'll develop a customized care plan based on your medical needs.</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8">
                  <h3 className="font-semibold text-gray-900 mb-2">Need immediate assistance?</h3>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://api.whatsapp.com/send?phone=+917703934446&text=Hello%20Team,%20Please%20call%20back.%20Looking%20for%20treatment%20in%20India."
                      className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaWhatsapp />
                      Contact via WhatsApp
                    </a>
                    <a
                      href="tel:+917703934446"
                      className="flex items-center gap-2 bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      <MessageCircle size={18} />
                      Call +91 770 393 4446
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="md:flex-1 bg-green-50 p-8 md:p-12 flex flex-col justify-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Download Our Mobile App</h2>
                <p className="text-gray-600 mb-6">
                  Get faster access to your medical journey with our mobile app. Track appointments, communicate with your care team, and access your medical records.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.rxone"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-gray-900 text-white px-5 py-3 rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    <FaGooglePlay className="text-2xl" />
                    <div>
                      <div className="text-xs opacity-80">GET IT ON</div>
                      <div className="font-medium">Google Play</div>
                    </div>
                  </a>
                  
                  <a
                    href="https://apps.apple.com/in/app/rxone-care-companion/id6479252685"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-gray-900 text-white px-5 py-3 rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    <FaApple className="text-2xl" />
                    <div>
                      <div className="text-xs opacity-80">Download on the</div>
                      <div className="font-medium">App Store</div>
                    </div>
                  </a>
                </div>
                
                <div className="flex justify-center">
                  <img 
                    src={appScreenshot} 
                    alt="RxOne Mobile App" 
                    className="max-w-full rounded-lg shadow-lg max-h-72 object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <Link 
              to="/hospitals/fortis" 
              className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-medium"
            >
              <FaArrowLeft className="text-sm" />
              Return to Fortis Hospital
            </Link>
          </div>
        </div>
      </main>
      
      <footer className="bg-gray-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img src={rxLogo} alt="RxOne Logo" className="h-10 mx-auto mb-4" />
          <p className="text-gray-400">© {new Date().getFullYear()} Rx One Care Pvt. Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ThankYouPage; 